import { ReactNode, RefObject, useCallback, useRef, useState } from "react"

import { useClickAway } from "react-use"

import { useEventListener } from "$/hooks/use-event-listener"
import useMediaQuery from "$/hooks/use-media-query"
import { Option } from "$/models/option"

import { buildCssClassName } from "@/3514/utils"
import { MAX_WIDTH_MOBILE_MEDIA } from "@/constants"
import { TUseEscapePress, useEscapePress } from "@/hooks"
import { TEmptyCallback } from "@/shared/types/functions"

import ContextMenu from "../menu/ContextMenu"

import Card from "./Card"

type Props = {
    className?: string
    menuItems: Option[]
    menuPosition?: "right" | "left" | "top" | "bottom"
    menuClassName?: string
    cardClassName?: string
    children: ReactNode
    showThreeDots?: boolean
    showContextMenu?: boolean
    menuOffsets?: Offset
    onClickOutside?: () => void
    onMenuItemClick: (item: Option) => void
}

type Offset = {
    right?: number
    left?: number
    top?: number
    bottom?: number
}

const OFFSET_TOP_MOBILE = 50
const OFFSET_LEFT_MOBILE = 80

const getPosition = (
    position: "right" | "left" | "top" | "bottom",
    target: HTMLElement,
    // eslint-disable-next-line default-param-last
    offsets: Offset = {},
    isMobile: boolean,
    _menuTarget: HTMLElement
) => {
    const { right = 0, left = 0, top = 0 } = offsets

    const targetHtml = target as HTMLElement

    if (!targetHtml) return {}

    if (isMobile) {
        return {
            top: `${OFFSET_TOP_MOBILE + top}px`,
            left: `${target.clientLeft + target.offsetWidth - OFFSET_LEFT_MOBILE + right}px`,
            transform: "translateX(-50%)"
        }
    }

    switch (position) {
        case "top":
            return {
                top: `${-target.offsetTop + top}px`,
                left: `${target.clientLeft + target.clientWidth / 2 + right}px`,
                transform: "translateX(-50%)"
            }
        case "bottom":
            return {
                top: `${target.offsetTop + target.offsetHeight + 10 + top}px`,
                left: `${target.clientLeft + target.offsetWidth + right}px`
            }
        case "left":
            return {
                top: `${top}px`,
                left: `${target.clientLeft - 200 + left}px`
            }
        case "right":
            return {
                top: `${top}px`,
                left: `${target.clientLeft + target.offsetWidth + right}px`
            }
        default:
            return {}
    }
}

function useOnKeyDownOutside<T extends HTMLElement = HTMLElement>(
    ref: RefObject<T>,
    handler: (event: KeyboardEvent) => void,
    keyboardEvent: "keydown" = "keydown"
): void {
    useEventListener(keyboardEvent, event => {
        const el = ref?.current

        if (!el || el.contains(event.target as Node)) {
            return
        }

        handler(event)
    })
}

const WithMenuCard = ({
    className = "",
    menuItems,
    menuClassName = "",
    cardClassName = "",
    menuPosition = "right",
    menuOffsets = {},
    showThreeDots = true,
    showContextMenu = true,
    children,
    onMenuItemClick,
    onClickOutside
}: Props) => {
    const ref = useRef<HTMLDivElement>(null)
    const menuRef = useRef<HTMLUListElement>(null)
    const [isOpenMenu, setIsOpenMenu] = useState(false)
    const isMobile = useMediaQuery(MAX_WIDTH_MOBILE_MEDIA)

    const onMenuClick: TEmptyCallback = useCallback((): void => setIsOpenMenu(open => !open), [])

    useClickAway(ref, _event => {
        setIsOpenMenu(false)
        onClickOutside?.()
    })

    useOnKeyDownOutside(ref, _event => {
        setIsOpenMenu(false)
        onClickOutside?.()
    })

    const { handleEscapePress }: TUseEscapePress = useEscapePress.call({ cb: (): void => setIsOpenMenu(false) })

    return (
        <div
            role="presentation"
            ref={ref}
            onKeyDown={handleEscapePress}
            className={buildCssClassName("position-relative cursor-pointer", className)}
        >
            <Card className={cardClassName} withMenu onMenuClick={onMenuClick} showMenu={showThreeDots}>
                {children}
            </Card>
            {isOpenMenu && showContextMenu && (
                <ContextMenu
                    ref={menuRef}
                    items={menuItems}
                    onClick={onMenuItemClick}
                    styles={getPosition(menuPosition, ref.current, menuOffsets, isMobile, menuRef.current)}
                    className={buildCssClassName("context-menu-card", menuClassName)}
                />
            )}
        </div>
    )
}

export default WithMenuCard
